import {
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileDoneOutlined,
  MailFilled,
  SendOutlined
} from "@ant-design/icons"
import { Button, type ButtonProps, Flex, Tooltip } from "antd"
import React, { useState } from "react"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../../constants/routes.ts"
import { CONTRACT_STATUSES } from "../../../../Contracts/constants/contract-statuses-keys.ts"
import type {
  LeadContract,
  LeadDocumentAttachment
} from "../../../types/lead.documents.types.ts"

type ActionKey =
  | "duplicate"
  | "view_pdf"
  | "download"
  | "edit"
  | "mark_as_sent"
  | "share"
  | "send_to_review"
  | "view_contract"

interface ActionConfig {
  key: ActionKey
  title: string
  icon: React.ReactNode
  onClick?: () => void | Promise<void>
  href?: string
  link?: string
  variant?: ButtonProps["variant"]
  color?: ButtonProps["color"]
  visible?: boolean
  hasLoading?: boolean
}

interface LeadViewContractItemActionsProps {
  contract: LeadContract
  isUploadedFile: boolean
  viewOnly: boolean
  isUpdateAllowed: boolean
  onViewContract: (contract: LeadContract) => void
  onDuplicateContract: (contract: LeadContract) => void
  onViewPDF: (attachment: LeadDocumentAttachment) => void
  onMarkAsSent: (contractId: string) => Promise<void>
  onShare: (contract: LeadContract) => void
  onSendToReview: (contractId: string, attachmentId: string) => Promise<void>
}

export function LeadViewContractItemActions({
  contract,
  viewOnly,
  isUpdateAllowed,
  isUploadedFile,
  onViewContract,
  onDuplicateContract,
  onViewPDF,
  onSendToReview,
  onMarkAsSent,
  onShare
}: LeadViewContractItemActionsProps) {
  const [loadingAction, setLoadingAction] = useState<ActionKey | "">("")
  const isCancelled = contract.status === CONTRACT_STATUSES.CANCELLED

  const latestAttachment =
    contract?.latest_attachment || contract?.attachments?.at(-1)
  const canSubmitForReview = contract?.can_submit_for_review && isUpdateAllowed
  // const canMarkAsSent =
  //   contract?.status === CONTRACT_STATUSES.APPROVED &&
  //   !!contract?.latest_attachment

  const canMarkAsSent = !!contract?.can_send_to_client
  const isLoading = !!loadingAction

  // Anything other than an explicit `false` is treated as editable — a
  // legacy (V1) row that predates this field being populated must not lose
  // its Edit button. A superseded version (`is_editable: false`) still has
  // View/Download; only Edit and Duplicate (branching a new version off a
  // non-latest one makes no sense in a linear version history) are gated.
  const isEditable = contract?.is_editable !== false

  const editHref = contract?.is_version_2
    ? RoutesPaths.contractDocument.edit(contract?.id)
    : RoutesPaths.contract.edit(contract?.id)

  const actions: ActionConfig[] = latestAttachment
    ? [
        {
          key: "duplicate",
          title: "Duplicate latest version",
          icon: <CopyOutlined />,
          onClick: () => onDuplicateContract(contract),
          hasLoading: true,
          visible:
            !viewOnly &&
            !isCancelled &&
            !isUploadedFile &&
            isUpdateAllowed &&
            isEditable
        },
        {
          key: "view_pdf",
          title: "View latest version",
          icon: <EyeOutlined />,
          onClick: () => onViewPDF(latestAttachment),
          variant: "outlined"
        },
        {
          key: "download",
          title: "Download latest version",
          icon: <DownloadOutlined />,
          href: latestAttachment?.file_url
        },
        {
          key: "edit",
          title: "Edit latest version",
          icon: <EditOutlined />,
          link: editHref,
          visible:
            !viewOnly && canSubmitForReview && !isUploadedFile && isEditable
        },
        {
          key: "send_to_review",
          title: "Send to review latest version",
          icon: <SendOutlined />,
          onClick: () => onSendToReview(contract?.id, latestAttachment?.id),
          variant: "outlined",
          color: "primary",
          visible: !viewOnly && canSubmitForReview,
          hasLoading: true
        },
        {
          key: "mark_as_sent",
          title: "Mark as sent",
          icon: <FileDoneOutlined />,
          onClick: () => onMarkAsSent(contract?.id),
          variant: "outlined",
          color: "cyan",
          visible: !viewOnly && canMarkAsSent,
          hasLoading: true
        },
        {
          key: "share",
          title: "Share via email",
          icon: <MailFilled />,
          onClick: () => onShare(contract),
          variant: "outlined",
          color: "cyan",
          visible:
            canMarkAsSent ||
            ([CONTRACT_STATUSES.SENT_TO_CLIENT] as string[])?.includes(
              contract?.status
            ),
          hasLoading: true
        }
      ]
    : [
        {
          key: "duplicate",
          title: "Duplicate",
          icon: <CopyOutlined />,
          onClick: () => onDuplicateContract(contract),
          hasLoading: true,
          visible: !viewOnly && !isCancelled && isUpdateAllowed && isEditable
        },
        {
          key: "view_contract",
          title: "View",
          icon: <EyeOutlined />,
          onClick: () => onViewContract(contract),
          variant: "outlined",
          color: "primary",
          hasLoading: true
        },
        {
          key: "edit",
          title: "Edit",
          icon: <EditOutlined />,
          link: editHref,
          visible: !viewOnly && isUpdateAllowed && isEditable
        }
      ]

  const handleClick = async (action: ActionConfig, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!action.onClick) return

    if (action.hasLoading) {
      setLoadingAction(action.key)
      try {
        await action.onClick()
      } finally {
        setLoadingAction("")
      }
    } else {
      action.onClick()
    }
  }

  const renderButton = (action: ActionConfig) => {
    const isActionLoading = loadingAction === action.key
    const disabled = action.hasLoading
      ? isLoading && !isActionLoading
      : isLoading

    return action.link ? (
      <Link to={action.link} onClick={(e) => e.stopPropagation()}>
        <Button
          icon={action.icon}
          variant={action.variant}
          color={action.color}
          disabled={disabled}
          loading={action.hasLoading && isActionLoading}
        />
      </Link>
    ) : (
      <Button
        icon={action.icon}
        onClick={(e) => handleClick(action, e)}
        href={action.href}
        target={action.href ? "_blank" : undefined}
        rel={action.href ? "noreferrer" : undefined}
        download={!!action.href}
        variant={action.variant}
        color={action.color}
        disabled={disabled}
        loading={action.hasLoading && isActionLoading}
      />
    )
  }

  return (
    <Flex gap="small" wrap className="shrink-0">
      {actions
        .filter((action) => action.visible !== false)
        .map((action) => (
          <Tooltip key={action.key} title={action.title}>
            {renderButton(action)}
          </Tooltip>
        ))}
    </Flex>
  )
}
